import { useEffect, useState, useCallback } from "react";
import { Mail, MailOpen, Trash2, Building2, Phone, Loader2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function PlatformContacts() {
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState(null);

  const load = useCallback(() => {
    api.get("/platform/contacts").then(({ data }) => setItems(data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = async (c) => {
    setSel(c);
    if (!c.read) {
      await api.post(`/platform/contacts/${c.id}/read`);
      load();
      window.dispatchEvent(new Event("wcc:contacts"));
    }
  };

  const remove = async (id) => {
    await api.delete(`/platform/contacts/${id}`);
    if (sel?.id === id) setSel(null);
    load();
    window.dispatchEvent(new Event("wcc:contacts"));
    toast.success("削除しました");
  };

  if (!items) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;

  const unread = items.filter((c) => !c.read).length;

  return (
    <div data-testid="platform-contacts">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">お問い合わせ受信箱</h1>
        <p className="text-sm text-slate-400 mt-1">公開サイトのお問い合わせフォームから届いたメッセージ（未読 {unread} 件）</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 bg-slate-900 rounded-xl border border-slate-800 divide-y divide-slate-800 overflow-hidden max-h-[72vh] overflow-y-auto">
          {items.length ? items.map((c) => (
            <button key={c.id} onClick={() => open(c)} data-testid={`contact-item-${c.id}`}
              className={`w-full text-left p-4 hover:bg-slate-800 transition-colors ${sel?.id === c.id ? "bg-slate-800" : ""}`}>
              <div className="flex items-center gap-2">
                {c.read ? <MailOpen className="w-4 h-4 text-slate-600 shrink-0" /> : <Mail className="w-4 h-4 text-amber-400 shrink-0" />}
                <span className={`text-sm truncate ${c.read ? "text-slate-400" : "font-semibold text-white"}`}>{c.name}</span>
                {!c.read && <span className="ml-auto w-2 h-2 rounded-full bg-amber-400 shrink-0" />}
              </div>
              <div className="text-xs text-slate-500 mt-1 truncate">{c.organization || c.email}</div>
              <div className="text-[11px] text-slate-600 mt-1">{(c.created_at || "").slice(0, 16).replace("T", " ")}</div>
            </button>
          )) : <div className="p-10 text-center text-slate-500 text-sm">お問い合わせはまだありません</div>}
        </div>

        <div className="lg:col-span-2 bg-slate-900 rounded-xl border border-slate-800 p-6 min-h-[300px]">
          {sel ? (
            <div className="wcc-fadein">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold text-white">{sel.name}</h2>
                    {sel.plan_name && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-semibold" data-testid="contact-plan-badge">プラン申込</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{(sel.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(sel.id)} data-testid={`contact-delete-${sel.id}`}
                  className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10">
                  <Trash2 className="w-4 h-4 mr-1" /> 削除
                </Button>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                {sel.plan_name && <div className="flex items-center gap-2 text-amber-300 sm:col-span-2 font-medium" data-testid="contact-plan-name">希望プラン：{sel.plan_name}</div>}
                {sel.organization && <div className="flex items-center gap-2 text-slate-300"><Building2 className="w-4 h-4 text-slate-500" /> {sel.organization}</div>}
                <a href={`mailto:${sel.email}`} className="flex items-center gap-2 text-sky-400 hover:underline"><Mail className="w-4 h-4 text-slate-500" /> {sel.email}</a>
                {sel.phone && <div className="flex items-center gap-2 text-slate-300"><Phone className="w-4 h-4 text-slate-500" /> {sel.phone}</div>}
              </div>
              <div className="mt-6 border-t border-slate-800 pt-5">
                <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{sel.message}</p>
              </div>
            </div>
          ) : <div className="text-slate-500 text-sm flex items-center justify-center h-full">メッセージを選択してください</div>}
        </div>
      </div>
    </div>
  );
}
